
import React, { Fragment, ReactElement, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl/dist';
import Button from '@cmsgov/design-system-core/dist/components/Button/Button';

// Components

import Form from '../../../lib/wrappers/Form';
import ModalHeader from '../../common/headers/ModalHeader';
import NamesInputs from '../../../components/ssap/pages/pageIncludes/common/names/NamesInputs';
import UniversalInput from '../../../lib/wrappers/UniversalInput';

// Utils

import { normalizeNameOfPerson } from '../../../utils/normalize/normalizeNameOfPerson';
import { cloneDeep } from '../../../utils/miscellaneous/objectHelpers';

// Constants

import { DROPDOWN_OPTIONS } from '../../../constants/ssap/hhmTypes/dropdownOptions';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '../../../constants/inputs/validationTypes';


// Types

import { CommonModalProps, HraDetails } from '../SsapTypes';

interface AddHraDetailsModal extends CommonModalProps {
    modal: {
        modalData: {
            actions: {
                updateHraDetails: Function
            },
            hraDetails: HraDetails,
            idxInHra: number,
            pageToReturn: number
        }
    },
}

const AddHraDetailsModal = (props: AddHraDetailsModal): ReactElement => {

    let {
        actions: {
            hideModal
        },
        bottomAnchors: [
            anchor_1,
        ],
        boundAnchor,
        formName = '',
        householdMembers,
        methods = {},
        modal: {
            modalData: {
                actions: {
                    updateHraDetails
                },
                hraDetails,
                idxInHra,
                pageToReturn
            }
        },
        modalHeaderProps
    } = props;

    const {
        hhmIndex : member,
        currentHhm
    } = props;

    const {
        name
    } = currentHhm;

    const [ areChangesApplied, changeStatus ] = useState<boolean>(false);
    const [ localHraState, setLocalHraState ] = useState<HraDetails>(cloneDeep(hraDetails));

    const {
        cityName,
        email,
        employerIdentificationNumber,
        employerPhoneNumber,
        name: {
            firstName,
            lastName,
            middleName,
            suffix
        },
        zipCode,
        stateCode,
        streetName1,
        streetName2,
    } = localHraState;

    const handleSaveClick = (): void => {

        if(!(formName in methods)) {
            return;
        }

        if(!areChangesApplied) {
            hideModal();
            return;
        }

        methods[formName]
            .onFormSubmitForValidationOnly([
                () => updateHraDetails(localHraState, idxInHra, undefined, []),
                () => hideModal()
            ]);
    };

    const valuesForWatch: string = householdMembers[member].healthReimbursementArrangement.length === 0
        ? 'no values'
        : householdMembers[member].healthReimbursementArrangement.map(value => Object.values(value).toString()).toString();

    useEffect((): void => {
        if(valuesForWatch !== 'no values' && areChangesApplied) {
            changeStatus(false);
            methods[formName].onFormSubmit(pageToReturn,
                [ () => hideModal() ]
            );
        }
    }, [ valuesForWatch ]);

    const onFieldChange = (newData: HraDetails): void => {
        setLocalHraState({ ...newData });
        !areChangesApplied && changeStatus(true);
    };

    return (
        <Fragment>
            <div className = 'gi-modal__header__head' >
                <ModalHeader { ...modalHeaderProps } />
            </div>
            <div
                className = 'gi-modal__body'
                id = 'hraDetailsModal'
            >
                <Form { ...{ formName } }>
                    <FormattedMessage
                        id = 'modals.employer_coverage_details.who_can_we_contact'
                        values = { {
                            personName: normalizeNameOfPerson(name)
                        } }
                        tagName = 'h3'
                    />
                    <NamesInputs
                        {...{
                            firstName,
                            middleName,
                            lastName,
                            suffix,
                            currentData: localHraState,
                            fields: [ 'name' ],
                            isRequired: false,
                            inputActions: {
                                onFieldChange
                            },
                            underFormControl: true
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.phone',
                            labelClasses: [ 'spl__width--01' ]
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                            name: 'employerPhoneNumber',
                            value: employerPhoneNumber,
                            placeholder: 'ssap.input.placeholder.phone',
                            validationType: VALIDATION_TYPES.PHONE_NUMBER,
                            currentData: localHraState,
                            fields: []
                        } }
                        inputActions = { {
                            onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.email',
                            labelClasses: [ 'spl__width--01' ]
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'email',
                            value: email,
                            placeholder: 'ssap.input.placeholder.email',
                            currentData: localHraState,
                            fields: []
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.address_line_one',
                            labelClasses: [ 'spl__width--01' ]
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'streetName1',
                            value: streetName1,
                            placeholder: 'ssap.input.placeholder.address_line_one',
                            validationType: VALIDATION_TYPES.ADDRESS_LINE_OPTIONAL,
                            currentData: localHraState,
                            fields: [ ]
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />

                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.address_line_two',
                            labelClasses: [ 'spl__width--01' ]
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'streetName2',
                            value: streetName2,
                            placeholder: 'ssap.input.placeholder.address_line_two',
                            validationType: VALIDATION_TYPES.ADDRESS_LINE_OPTIONAL,
                            currentData: localHraState,
                            fields: [ ]
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.city',
                            labelClasses: [ 'spl__width--01' ]
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'cityName',
                            value: cityName,
                            placeholder: 'ssap.input.placeholder.city',
                            validationType: VALIDATION_TYPES.CITY_OPTIONAL,
                            currentData: localHraState,
                            fields: [ ]
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.zip'
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'zipCode',
                            value: zipCode,
                            placeholder: 'ssap.input.placeholder.zip',
                            validationType: VALIDATION_TYPES.ZIP_CODE_5_DIGITS,
                            currentData: localHraState,
                            fields: [ ]
                        }}
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.state',
                            isLabelRequired: false
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.DROPDOWN,
                            name: 'stateCode',
                            value: stateCode,
                            options: DROPDOWN_OPTIONS.STATE,
                            validationType: VALIDATION_TYPES.CHOSEN,
                            currentData: localHraState,
                            fields: [ ]
                        }}
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.ein'
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'employerIdentificationNumber',
                            value: employerIdentificationNumber,
                            placeholder: 'ssap.input.placeholder.ein',
                            currentData: localHraState,
                            fields: [ ]
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                    />
                </Form>

            </div>
            <div className = 'gi-modal__footer' >
                <div className = 'grid-row controls' >
                    <div className = 'grid-offset-4 txt-left' >
                        <Button
                            className = 'usa-button usa-button--outline gi-button gi-button__outline'
                            onClick = { () => hideModal() }
                        >
                            <FormattedMessage id = 'button.cancel' />
                        </Button>
                        <Button
                            className = 'usa-button gi-button gi-button__primary'
                            onClick = { handleSaveClick }
                            inputRef = { boundAnchor(anchor_1) }
                        >
                            <FormattedMessage id='button.save' />
                        </Button>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default AddHraDetailsModal;
